/**
 * GroVita Quick View Modal
 * Opens a product preview modal when "Quick View" is clicked on a product card.
 *
 * - Fetches product data via Shopify /products/[handle].js
 * - Shows images, title, price, description, variants, and ATC
 * - Fully handles cart via Section Rendering API (same as card ATC handler)
 * - Accessible: focus trap, Escape to close, aria attributes
 */

(function () {
  'use strict';

  const MODAL_ID = 'grovita-quick-view-modal';

  /* ---------------------------------------------------------------
     Build modal DOM once and insert into body
  --------------------------------------------------------------- */
  function buildModal() {
    if (document.getElementById(MODAL_ID)) return;

    const el = document.createElement('div');
    el.id = MODAL_ID;
    el.className = 'grovita-qv-modal';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Product quick view');
    el.setAttribute('hidden', '');

    el.innerHTML = `
      <div class="grovita-qv-modal__backdrop" aria-hidden="true"></div>
      <div class="grovita-qv-modal__container">

        <button class="grovita-qv-modal__close" type="button" aria-label="Close quick view">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div class="grovita-qv-modal__body">

          <!-- LEFT: images -->
          <div class="grovita-qv-modal__image-col">
            <div class="grovita-qv-modal__image-wrap">
              <img class="grovita-qv-modal__image" src="" alt="" loading="lazy">
            </div>
            <div class="grovita-qv-modal__thumbnails"></div>
          </div>

          <!-- RIGHT: product info -->
          <div class="grovita-qv-modal__info-col">

            <!-- Loading spinner -->
            <div class="grovita-qv-modal__loader" aria-live="polite" aria-label="Loading product">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" stroke-dasharray="31.42" stroke-dashoffset="10">
                  <animateTransform attributeName="transform" type="rotate"
                    from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>

            <!-- Product content (shown after fetch) -->
            <div class="grovita-qv-modal__content" hidden>
              <p class="grovita-qv-modal__vendor"></p>
              <h2 class="grovita-qv-modal__title"></h2>

              <div class="grovita-qv-modal__price-wrap">
                <s class="grovita-qv-modal__compare-price" hidden></s>
                <span class="grovita-qv-modal__price"></span>
              </div>

              <p class="grovita-qv-modal__description"></p>

              <div class="grovita-qv-modal__variants"></div>

              <div class="grovita-qv-modal__actions">
                <form class="grovita-qv-modal__form" method="post" action="/cart/add" data-grovita-qv-form>
                  <input type="hidden" class="grovita-qv-modal__variant-input" name="id" value="">
                  <input type="hidden" name="quantity" value="1">
                  <button type="submit" class="grovita-qv-modal__atc-btn">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>
                      <path d="M3 6h18"/>
                      <path d="M16 10a4 4 0 0 1-8 0"/>
                    </svg>
                    <span class="grovita-qv-modal__atc-text">Add to Cart</span>
                  </button>
                </form>
                <a class="grovita-qv-modal__full-link" href="#">View Full Details →</a>
              </div>
            </div>

          </div>
        </div>
      </div>
    `;

    document.body.appendChild(el);
  }

  /* ---------------------------------------------------------------
     Helpers
  --------------------------------------------------------------- */
  function sizedUrl(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (_) {
      return url;
    }
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  /* ---------------------------------------------------------------
     Populate modal with fetched product data
  --------------------------------------------------------------- */
  function populate(modal, product) {
    // — images —
    const imageEl    = modal.querySelector('.grovita-qv-modal__image');
    const thumbsEl   = modal.querySelector('.grovita-qv-modal__thumbnails');
    const images     = product.images || [];

    if (images.length > 0) {
      imageEl.src = sizedUrl(images[0], 600);
      imageEl.alt = product.title;
    }

    thumbsEl.innerHTML = '';
    if (images.length > 1) {
      images.slice(0, 6).forEach((src, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'grovita-qv-modal__thumb' + (i === 0 ? ' is-active' : '');
        btn.setAttribute('aria-label', 'View image ' + (i + 1));

        const img = document.createElement('img');
        img.src = sizedUrl(src, 120);
        img.alt = '';
        btn.appendChild(img);

        btn.addEventListener('click', () => {
          imageEl.src = sizedUrl(src, 600);
          thumbsEl.querySelectorAll('.grovita-qv-modal__thumb').forEach(t => t.classList.remove('is-active'));
          btn.classList.add('is-active');
        });

        thumbsEl.appendChild(btn);
      });
    }

    // — vendor —
    const vendorEl = modal.querySelector('.grovita-qv-modal__vendor');
    vendorEl.textContent = product.vendor || '';
    vendorEl.hidden = !product.vendor;

    // — title —
    modal.querySelector('.grovita-qv-modal__title').textContent = product.title;

    // — price (first variant / min price) —
    const compareEl = modal.querySelector('.grovita-qv-modal__compare-price');
    const priceEl   = modal.querySelector('.grovita-qv-modal__price');
    const firstV    = product.variants[0];

    updatePrice(priceEl, compareEl, firstV.price, firstV.compare_at_price);

    // — description —
    const descEl = modal.querySelector('.grovita-qv-modal__description');
    if (product.description) {
      const plain = product.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      descEl.textContent = plain.length > 220 ? plain.slice(0, 220) + '…' : plain;
      descEl.hidden = false;
    } else {
      descEl.hidden = true;
    }

    // — variants —
    const variantsEl    = modal.querySelector('.grovita-qv-modal__variants');
    const variantInput  = modal.querySelector('.grovita-qv-modal__variant-input');
    variantsEl.innerHTML = '';
    variantInput.value   = firstV.id;

    if (product.variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'grovita-qv-modal__variant-select';
      select.setAttribute('aria-label', 'Choose variant');

      product.variants.forEach(v => {
        const opt    = document.createElement('option');
        opt.value    = v.id;
        opt.textContent = v.title + (v.available ? '' : ' — Sold out');
        opt.disabled = !v.available;
        select.appendChild(opt);
      });

      select.addEventListener('change', () => {
        const chosen = product.variants.find(v => String(v.id) === select.value);
        if (!chosen) return;
        variantInput.value = chosen.id;
        updatePrice(priceEl, compareEl, chosen.price, chosen.compare_at_price);
        const atcBtn = modal.querySelector('.grovita-qv-modal__atc-btn');
        const atcTxt = modal.querySelector('.grovita-qv-modal__atc-text');
        atcBtn.disabled = !chosen.available;
        atcTxt.textContent = chosen.available ? 'Add to Cart' : 'Sold Out';
      });

      variantsEl.appendChild(select);
    }

    // — ATC button state —
    const atcBtn = modal.querySelector('.grovita-qv-modal__atc-btn');
    const atcTxt = modal.querySelector('.grovita-qv-modal__atc-text');
    atcBtn.disabled = !firstV.available;
    atcTxt.textContent = firstV.available ? 'Add to Cart' : 'Sold Out';

    // — full details link —
    modal.querySelector('.grovita-qv-modal__full-link').href = '/products/' + product.handle;

    // — show content —
    const loader = modal.querySelector('.grovita-qv-modal__loader');
    loader.hidden = true;
    loader.style.display = 'none'; // belt-and-suspenders: class display:flex overrides [hidden] attr
    modal.querySelector('.grovita-qv-modal__content').hidden = false;
  }

  function updatePrice(priceEl, compareEl, price, compareAtPrice) {
    priceEl.textContent = formatMoney(price);
    if (compareAtPrice && compareAtPrice > price) {
      compareEl.textContent = formatMoney(compareAtPrice);
      compareEl.hidden = false;
      priceEl.classList.add('grovita-qv-modal__price--sale');
    } else {
      compareEl.hidden = true;
      priceEl.classList.remove('grovita-qv-modal__price--sale');
    }
  }

  /* ---------------------------------------------------------------
     Open / close
  --------------------------------------------------------------- */
  function openModal(handle) {
    buildModal();
    const modal = document.getElementById(MODAL_ID);

    // Reset to loading state
    const loaderEl = modal.querySelector('.grovita-qv-modal__loader');
    loaderEl.hidden = false;
    loaderEl.style.removeProperty('display'); // clear previous inline hide so spinner shows again
    modal.querySelector('.grovita-qv-modal__content').hidden = true;
    modal.querySelector('.grovita-qv-modal__thumbnails').innerHTML = '';

    modal.removeAttribute('hidden');
    document.body.classList.add('grovita-qv-open');

    // Focus close button after animation
    setTimeout(() => modal.querySelector('.grovita-qv-modal__close').focus(), 80);

    fetch('/products/' + handle + '.js')
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(product => populate(modal, product))
      .catch(err => {
        console.error('[GroVita QV] Could not load product:', err);
        const loader = modal.querySelector('.grovita-qv-modal__loader');
        loader.innerHTML = '<p style="font-family:sans-serif;color:#c00;padding:16px">Could not load product. Please try again.</p>';
      });
  }

  function closeModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.classList.remove('grovita-qv-open');
  }

  /* ---------------------------------------------------------------
     ATC handler — mirrors grovitaInitAddToCart but for modal form
  --------------------------------------------------------------- */
  function handleModalAtc(e) {
    const form = e.target.closest('[data-grovita-qv-form]');
    if (!form) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    const btn       = form.querySelector('.grovita-qv-modal__atc-btn');
    const txtEl     = form.querySelector('.grovita-qv-modal__atc-text');
    const variantId = parseInt(form.querySelector('[name="id"]').value, 10);
    const quantity  = parseInt(form.querySelector('[name="quantity"]').value || '1', 10);

    if (!variantId || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');

    const cartUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(cartUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname,
      }),
    })
      .then(r => {
        if (!r.ok) throw new Error('Cart error ' + r.status);
        return r.json();
      })
      .then(data => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        txtEl.textContent = '✓ Added!';

        // Update cart drawer if present (same logic as card ATC handler)
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && data.sections && data.sections['cart-drawer']) {
          const cartDrawerEl = document.getElementById('CartDrawer');
          if (cartDrawerEl) {
            const parsed   = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
            const newInner = parsed.querySelector('#CartDrawer');
            if (newInner) cartDrawerEl.innerHTML = newInner.innerHTML;
          }
          const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', () => cartDrawer.close());
          cartDrawer.classList.remove('is-empty');
        }

        // PubSub event for cart icon badge
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'grovita-quick-view',
            productVariantId: variantId,
            cartData: data,
          });
        }

        // Close modal after short delay
        setTimeout(() => {
          closeModal();
          btn.classList.remove('is-added');
          txtEl.textContent = 'Add to Cart';
          // Open cart drawer
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }, 1200);
      })
      .catch(err => {
        console.error('[GroVita QV] Add to cart failed:', err);
        btn.classList.remove('is-loading');
        txtEl.textContent = 'Error — try again';
        setTimeout(() => { txtEl.textContent = 'Add to Cart'; }, 3000);
      });
  }

  /* ---------------------------------------------------------------
     Initialise event listeners (idempotent)
  --------------------------------------------------------------- */
  let _initialised = false;

  function init() {
    if (_initialised) return;
    _initialised = true;

    buildModal();
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    // Close: backdrop + button + Escape
    modal.querySelector('.grovita-qv-modal__backdrop').addEventListener('click', closeModal);
    modal.querySelector('.grovita-qv-modal__close').addEventListener('click', closeModal);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });

    // ATC form submit (modal only)
    document.addEventListener('submit', handleModalAtc);

    // Quick view button clicks — event delegation
    document.addEventListener('click', e => {
      const btn = e.target.closest('.grovita-product-card__quickview-btn, .grovita-product-card__side-btn--qv');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = btn.dataset.handle;
      if (handle) openModal(handle);
    });
  }

  // Run on DOMContentLoaded and Shopify section lifecycle
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
